import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { parseBatchExpiry, parseMoney, formatIsoDay } from '@wodi/domain';
import type { GrnView, PurchaseOrderView, StockItemView, SupplierView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { purchaseOrderQuery } from '../api/procurement.ts';
import { createGrn, postGrn, searchSuppliers, storeItemQuery, updateGrn } from '../api/stock.ts';
import { LoadFailed } from '../procurement/common.tsx';
import { centsToText } from '../procurement/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { BackToStore, errorText, newRequestKey, WithStore } from '../stock/common.tsx';
import { formatCents, formatQuantity, readQuantity } from '../stock/format.ts';
import { StockItemPicker } from '../stock/item-picker.tsx';
import { screen } from '../layout/screen.tsx';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/stores/$storeId/receive');

interface DraftLine {
  key: string;
  item: StockItemView;
  /** The LPO line this receives against, when receiving an LPO. */
  orderLineId?: string;
  batchNumber: string;
  expiryText: string;
  packUnitCode: string;
  packsText: string;
  costText: string;
}

/**
 * Receive goods (plan §5.3): supplier, their invoice or delivery note number, and one line
 * per batch as printed on the packs. Posting moves the stock into this store.
 */
export function ReceiveGoodsPage() {
  const { storeId } = route.useParams();
  const { orderId } = route.useSearch();
  return (
    <WithStore storeId={storeId}>
      {(store) =>
        orderId === undefined ? (
          <ReceiveForm storeId={store.id} storeName={store.name} />
        ) : (
          <ReceiveAgainstOrder storeId={store.id} storeName={store.name} orderId={orderId} />
        )
      }
    </WithStore>
  );
}

/**
 * Receiving an approved LPO (docs/plans/phase2-procurement.md §5.7): the supplier is the
 * LPO's, and each outstanding LPO line starts filled in with what is still to come.
 */
function ReceiveAgainstOrder({
  storeId,
  storeName,
  orderId,
}: {
  storeId: string;
  storeName: string;
  orderId: string;
}) {
  const order = useQuery(purchaseOrderQuery(orderId));
  const items = useQueries({
    queries: (order.data?.lines ?? []).map((l) => storeItemQuery(storeId, l.itemId)),
  });
  if (order.isPending || items.some((i) => i.isPending))
    return <LoadingBlock label="Loading the LPO" rows={4} />;
  const failed = order.error ?? items.find((i) => i.isError)?.error ?? null;
  if (failed !== null || !order.isSuccess)
    return (
      <LoadFailed
        error={failed}
        fallback="The LPO couldn't be loaded."
        onRetry={() => {
          void order.refetch();
          for (const i of items) void i.refetch();
        }}
      />
    );
  if (order.data.store.id !== storeId || order.data.status !== 'approved')
    return (
      <Notice tone="info">
        {order.data.status === 'approved'
          ? `${order.data.number} is for ${order.data.store.name}. Receive it there.`
          : `${order.data.number} is not open for receiving.`}
      </Notice>
    );
  const lines: DraftLine[] = order.data.lines.flatMap((l, index) => {
    const item = items[index]?.data?.item;
    if (item === undefined || l.outstanding === 0) return [];
    return [
      {
        key: newRequestKey(),
        item,
        orderLineId: l.id,
        batchNumber: '',
        expiryText: '',
        packUnitCode: l.packUnitCode,
        packsText: String(Math.ceil(l.outstanding / l.baseUnitsPerPack)),
        costText: centsToText(l.packPrice.cents),
      },
    ];
  });
  return (
    <ReceiveForm storeId={storeId} storeName={storeName} order={order.data} initialLines={lines} />
  );
}

function lineProblems(line: DraftLine): string[] {
  const problems: string[] = [];
  if (line.batchNumber.trim() === '') problems.push('Enter the batch number.');
  if (line.item.stock?.expiryTracked !== false) {
    const expiry = parseBatchExpiry(line.expiryText);
    if (!expiry.ok) problems.push('Enter the expiry as on the pack, like 03/2027.');
  }
  if (readQuantity(line.packsText) === null || readQuantity(line.packsText) === 0)
    problems.push('Enter how many packs arrived.');
  if (!parseMoney(line.costText).ok) problems.push('Enter the cost per pack, like 1,200.');
  return problems;
}

function ReceiveForm({
  storeId,
  storeName,
  order,
  initialLines = [],
}: {
  storeId: string;
  storeName: string;
  order?: PurchaseOrderView;
  initialLines?: DraftLine[];
}) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const suppliers = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => searchSuppliers(''),
  });
  const [supplierId, setSupplierId] = useState<string | null>(order?.supplier.id ?? null);
  const [documentNumber, setDocumentNumber] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>(initialLines);
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(newRequestKey);
  const [created, setCreated] = useState<GrnView | null>(null);
  const [shortReasons, setShortReasons] = useState<Record<string, string>>({});
  const [needsReason, setNeedsReason] = useState<number | null>(null);
  const [overReasons, setOverReasons] = useState<Record<string, string>>({});
  const [needsOverReason, setNeedsOverReason] = useState<OverReceipt | null>(null);

  const update = (key: string, patch: Partial<DraftLine>) => {
    setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const body = () => ({
    clientRequestId: requestKey,
    storeId,
    ...(order === undefined ? {} : { purchaseOrderId: order.id }),
    supplierId: supplierId ?? '',
    ...(documentNumber.trim() === '' ? {} : { supplierDocumentNumber: documentNumber.trim() }),
    receivedAt: new Date().toISOString(),
    ...(note.trim() === '' ? {} : { note: note.trim() }),
    lines: lines.map((l) => {
      const expiry = parseBatchExpiry(l.expiryText);
      const cost = parseMoney(l.costText);
      return {
        itemId: l.item.itemId,
        batchNumber: l.batchNumber.trim(),
        expiryDate: expiry.ok ? formatIsoDay(expiry.value.day) : null,
        packUnitCode: l.packUnitCode,
        packs: readQuantity(l.packsText) ?? 0,
        packCost: { currency: 'KES' as const, cents: cost.ok ? cost.value.cents.toString() : '0' },
        ...(l.orderLineId === undefined ? {} : { purchaseOrderLineId: l.orderLineId }),
      };
    }),
  });

  const post = useMutation({
    mutationFn: async () => {
      // Create the draft once; on a retry, save any edits to it before posting again.
      const all = body();
      const grn =
        created === null
          ? await createGrn(all)
          : await updateGrn(created.id, {
              expectedVersion: created.version,
              supplierId: all.supplierId,
              receivedAt: all.receivedAt,
              lines: all.lines,
              ...(all.supplierDocumentNumber === undefined
                ? {}
                : { supplierDocumentNumber: all.supplierDocumentNumber }),
              ...(all.note === undefined ? {} : { note: all.note }),
            });
      setCreated(grn);
      return postGrn(grn.id, grn.version, shortReasons, overReasons);
    },
    onSuccess: async (grn) => {
      await queryClient.invalidateQueries({ queryKey: ['stock', storeId] });
      toasts.show({ title: 'Goods received', description: grn.number });
      if (order === undefined) {
        void navigate({ to: '/stores', search: { storeId } });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      void navigate({ to: '/procurement/orders/$orderId', params: { orderId: order.id } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'short_expiry_reason_required') {
        const lineNo = error.context?.['lineNo'];
        if (typeof lineNo === 'number') setNeedsReason(lineNo);
      }
      if (error instanceof ApiError && error.code === 'over_receipt_reason_required') {
        const lineNo = error.context?.['lineNo'];
        const ordered = error.context?.['ordered'];
        const receivedAfter = error.context?.['receivedAfter'];
        if (
          typeof lineNo === 'number' &&
          typeof ordered === 'number' &&
          typeof receivedAfter === 'number'
        )
          setNeedsOverReason({ lineNo, ordered, receivedAfter });
      }
    },
  });

  const valid =
    supplierId !== null && lines.length > 0 && lines.every((l) => lineProblems(l).length === 0);
  const total = lines.reduce((sum, l) => {
    const cost = parseMoney(l.costText);
    const packs = readQuantity(l.packsText);
    return cost.ok && packs !== null ? sum + cost.value.cents * BigInt(packs) : sum;
  }, 0n);

  return (
    <div className={styles.page}>
      <BackToStore storeId={storeId} name={storeName} />
      <PageHeader
        title={
          order === undefined
            ? `Receive goods into ${storeName}`
            : `Receive ${order.number} into ${storeName}`
        }
      />
      {order === undefined ? null : (
        <p className={styles.meta}>
          Against LPO <span className="wodi-id">{order.number}</span> from {order.supplier.name}.
          Change the packs to what actually arrived; a line can be split into batches.
        </p>
      )}
      <form
        className={`${styles.wideForm} ${screen.panel}`}
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (valid) post.mutate();
        }}
      >
        <div className={styles.row}>
          <Select
            label="Supplier"
            placeholder={suppliers.isPending ? 'Loading suppliers…' : 'Choose a supplier'}
            options={(suppliers.data ?? []).map((s: SupplierView) => ({ id: s.id, label: s.name }))}
            value={supplierId}
            isDisabled={order !== undefined}
            onChange={(v) => {
              setSupplierId(String(v));
            }}
            {...(tried && supplierId === null ? { errorMessage: 'Choose the supplier.' } : {})}
            {...(suppliers.isSuccess && suppliers.data.length === 0
              ? { description: 'No suppliers yet. Add one in Settings, Stores and suppliers.' }
              : {})}
          />
          <TextField
            label="Supplier invoice or delivery note number"
            value={documentNumber}
            onChange={setDocumentNumber}
          />
        </div>

        {order !== undefined ? null : (
          <StockItemPicker
            label="Add an item"
            onSelect={(item) => {
              const firstPack = item.stock?.packUnits.find((p) => p.status === 'active');
              setLines((all) => [
                ...all,
                {
                  key: newRequestKey(),
                  item,
                  batchNumber: '',
                  expiryText: '',
                  packUnitCode: firstPack?.code ?? item.stock?.baseUnit.code ?? '',
                  packsText: '',
                  costText: '',
                },
              ]);
            }}
          />
        )}

        {lines.length === 0 ? (
          <p className={styles.meta}>No lines yet. Search for each item on the delivery.</p>
        ) : null}
        {lines.map((line, index) => {
          const stock = line.item.stock;
          const packs = [
            ...(stock?.packUnits.filter((p) => p.status === 'active') ?? []).map((p) => ({
              id: p.code,
              label: `${p.display} (${p.baseUnitsPerPack} ${stock?.baseUnit.display ?? ''})`,
            })),
            { id: stock?.baseUnit.code ?? '', label: `Single ${stock?.baseUnit.display ?? ''}` },
          ];
          const factor =
            stock?.packUnits.find((p) => p.code === line.packUnitCode)?.baseUnitsPerPack ?? 1;
          const packCount = readQuantity(line.packsText);
          const problems = tried ? lineProblems(line) : [];
          return (
            <fieldset key={line.key} className={styles.line}>
              <legend className={styles.lineHeading}>
                Line {index + 1}: {line.item.name}
              </legend>
              <div className={styles.row}>
                <TextField
                  label="Batch number"
                  value={line.batchNumber}
                  onChange={(v) => {
                    update(line.key, { batchNumber: v });
                  }}
                />
                {stock?.expiryTracked === false ? null : (
                  <TextField
                    label="Expiry"
                    description="As printed: 03/2027 or 15/03/2027"
                    value={line.expiryText}
                    onChange={(v) => {
                      update(line.key, { expiryText: v });
                    }}
                  />
                )}
                <Select
                  label="Pack"
                  options={packs}
                  value={line.packUnitCode}
                  onChange={(v) => {
                    update(line.key, { packUnitCode: String(v) });
                  }}
                />
                <TextField
                  label="Packs"
                  inputMode="numeric"
                  value={line.packsText}
                  onChange={(v) => {
                    update(line.key, { packsText: v });
                  }}
                />
                <TextField
                  label="Cost per pack"
                  unit="KES"
                  inputMode="decimal"
                  value={line.costText}
                  onChange={(v) => {
                    update(line.key, { costText: v });
                  }}
                />
              </div>
              <p className={styles.meta}>
                {packCount === null
                  ? ' '
                  : `${formatQuantity(packCount * factor, stock?.baseUnit.display ?? '')} into ${storeName}`}
              </p>
              {problems.length === 0 ? null : <Notice tone="error">{problems.join(' ')}</Notice>}
              {shortReasons[String(index + 1)] === undefined ? null : (
                <p className={styles.meta}>
                  Short expiry accepted: {shortReasons[String(index + 1)]}
                </p>
              )}
              {overReasons[String(index + 1)] === undefined ? null : (
                <p className={styles.meta}>
                  More than ordered accepted: {overReasons[String(index + 1)]}
                </p>
              )}
              <div className={styles.actions}>
                <Button
                  variant="quiet"
                  isDisabled={created !== null}
                  onPress={() => {
                    setLines((all) => all.filter((l) => l.key !== line.key));
                  }}
                >
                  Remove line
                </Button>
                {line.orderLineId === undefined ? null : (
                  <Button
                    variant="quiet"
                    isDisabled={created !== null}
                    onPress={() => {
                      setLines((all) => splitBatch(all, line.key));
                    }}
                  >
                    Add another batch
                  </Button>
                )}
              </div>
            </fieldset>
          );
        })}

        <TextArea label="Note (optional)" value={note} onChange={setNote} />
        <p className={styles.total}>Total {formatCents(total.toString())}</p>
        {post.error === null ||
        (post.error instanceof ApiError &&
          (post.error.code === 'short_expiry_reason_required' ||
            post.error.code === 'over_receipt_reason_required')) ? null : (
          <Notice tone="error">
            {errorText(post.error, "The goods weren't received. Try again.")}
          </Notice>
        )}
        {online ? null : (
          <Notice tone="info">
            Receiving goods needs a connection to Wodi. Reconnect, then post.
          </Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={post.isPending}
            pendingLabel="Posting…"
          >
            Post receipt
          </Button>
        </div>
      </form>
      {needsOverReason === null ? null : (
        <OverReceiptDialog
          over={needsOverReason}
          onCancel={() => {
            setNeedsOverReason(null);
          }}
          onAccept={(reason) => {
            setOverReasons((r) => ({ ...r, [String(needsOverReason.lineNo)]: reason }));
            setNeedsOverReason(null);
          }}
        />
      )}
      {needsReason === null ? null : (
        <ShortExpiryDialog
          lineNo={needsReason}
          onCancel={() => {
            setNeedsReason(null);
          }}
          onAccept={(reason) => {
            setShortReasons((r) => ({ ...r, [String(needsReason)]: reason }));
            setNeedsReason(null);
          }}
        />
      )}
    </div>
  );
}

/** Interruptive alert (CLAUDE.md): acknowledge with a reason, which is audited. */
function ShortExpiryDialog({
  lineNo,
  onCancel,
  onAccept,
}: {
  lineNo: number;
  onCancel: () => void;
  onAccept: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog
      title={`Line ${lineNo} expires soon`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <p>
        This batch expires within 180 days. Accept it only if you have a reason, for example nothing
        longer-dated is available. Then post the receipt again.
      </p>
      <TextArea label="Reason" value={reason} onChange={setReason} autoFocus />
      <DialogActions>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < 5}
          onPress={() => {
            onAccept(reason.trim());
          }}
        >
          Accept this batch
        </Button>
        <Button variant="quiet" onPress={onCancel}>
          Back
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface OverReceipt {
  lineNo: number;
  ordered: number;
  receivedAfter: number;
}

/** A second batch of the same LPO line, placed right after it. */
function splitBatch(all: DraftLine[], key: string): DraftLine[] {
  const at = all.findIndex((l) => l.key === key);
  const line = all[at];
  if (line === undefined) return all;
  const copy = { ...line, key: newRequestKey(), batchNumber: '', expiryText: '', packsText: '' };
  return [...all.slice(0, at + 1), copy, ...all.slice(at + 1)];
}

/** Interruptive alert (CLAUDE.md): more than the LPO ordered needs a reason, which is audited. */
function OverReceiptDialog({
  over,
  onCancel,
  onAccept,
}: {
  over: OverReceipt;
  onCancel: () => void;
  onAccept: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog
      title={`Line ${String(over.lineNo)} is more than was ordered`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <p>
        With this delivery {over.receivedAfter} base units will have been received against an order
        of {over.ordered}. Accept the extra only if you have a reason, then post the receipt again.
      </p>
      <TextArea label="Reason" value={reason} onChange={setReason} autoFocus />
      <DialogActions>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < 5}
          onPress={() => {
            onAccept(reason.trim());
          }}
        >
          Accept the extra
        </Button>
        <Button variant="quiet" onPress={onCancel}>
          Back
        </Button>
      </DialogActions>
    </Dialog>
  );
}
